import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useStytchSession } from "@stytch/react";
import { useTranslation } from "react-i18next";
import useUserSettings from "../queries/user/useUserSettings";
import { useLocalStorage } from "../hooks/useLocalStorage";
import {
  DISPLAY_LANGUAGES,
  DISPLAY_LANGUAGE_CODES,
} from "../constants/languages";
import { getBrowserPreferredLanguage } from "../utils/browserLanguage";

export interface A11ySettings {
  darkMode: boolean;
  highContrast: boolean;
  lowAnimation: boolean;
  dyslexiaFont: boolean;
  largeText: boolean;
  language: string; // Support all display language codes from DISPLAY_LANGUAGE_CODES
}

export interface A11yContextType {
  settings: A11ySettings;
  updateSettings: (newSettings: Partial<A11ySettings>) => void;
}

// Default context value for better HMR resilience
const defaultA11yContext: A11yContextType = {
  settings: {
    darkMode: false,
    highContrast: false,
    lowAnimation: false,
    dyslexiaFont: false,
    largeText: false,
    language: "en",
  },
  updateSettings: () => {
    console.warn("A11y: updateSettings called on default context");
  },
};

const A11yContext = createContext<A11yContextType | undefined>(undefined);

export function useA11y() {
  const context = useContext(A11yContext);

  if (context === undefined) {
    // In development, provide a more helpful error message and fallback
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "useA11y: Context is undefined. This might be due to hot module reloading. " +
          "Make sure the component is wrapped in A11yProvider."
      );

      // Return a fallback context during HMR to prevent crashes
      return {
        settings: {
          darkMode: false,
          highContrast: false,
          lowAnimation: false,
          dyslexiaFont: false,
          largeText: false,
          language: getBrowserPreferredLanguage(),
        },
        updateSettings: () => {
          console.warn(
            "useA11y: updateSettings called but context is not available"
          );
        },
      };
    }

    throw new Error("useA11y must be used within an A11yProvider");
  }

  return context;
}

export function useA11ySafe(): A11yContextType | null {
  const context = useContext(A11yContext);

  if (context === undefined) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "useA11ySafe: Context is undefined. This might be due to hot module reloading."
      );
    }
    return null;
  }

  return context;
}

export function useA11yWithFallback(): A11yContextType {
  const context = useContext(A11yContext);

  if (context === undefined) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "useA11yWithFallback: Using fallback context due to missing provider or HMR."
      );
    }

    return defaultA11yContext;
  }

  return context;
}

export function translateLanguageNameToShortCode(
  language: string
): A11ySettings["language"] | undefined {
  // Map database/API language names to i18n shortcodes using the comprehensive mapping
  const languageKey = Object.keys(DISPLAY_LANGUAGES).find(
    (key) => key.toLowerCase() === language.toLowerCase()
  );

  if (
    languageKey &&
    DISPLAY_LANGUAGE_CODES[languageKey as keyof typeof DISPLAY_LANGUAGE_CODES]
  ) {
    return DISPLAY_LANGUAGE_CODES[
      languageKey as keyof typeof DISPLAY_LANGUAGE_CODES
    ];
  }

  return undefined;
}

export function translateLanguageShortCodeToName(
  shortCode: A11ySettings["language"]
): string | undefined {
  // Map i18n shortcodes to display names using the comprehensive mapping
  const languageKey = Object.keys(DISPLAY_LANGUAGE_CODES).find(
    (key) =>
      DISPLAY_LANGUAGE_CODES[key as keyof typeof DISPLAY_LANGUAGE_CODES] ===
      shortCode
  );

  if (
    languageKey &&
    DISPLAY_LANGUAGES[languageKey as keyof typeof DISPLAY_LANGUAGES]
  ) {
    return DISPLAY_LANGUAGES[languageKey as keyof typeof DISPLAY_LANGUAGES];
  }

  return undefined;
}

export function translateA11yLanguageToI18next(
  a11yLanguage: A11ySettings["language"]
): string {
  const supportedLanguages = Object.values(DISPLAY_LANGUAGE_CODES);

  return (supportedLanguages as string[]).includes(a11yLanguage)
    ? a11yLanguage
    : "en";
}

export function A11yProvider({ children }: { children: React.ReactNode }) {
  const { session, fromCache } = useStytchSession();
  const { useGetUserSettings: useGetUser } = useUserSettings();
  const { i18n } = useTranslation();

  // Get user settings if logged in
  const { data: userSettings } = useGetUser({
    enabled: !!session && !fromCache,
  });

  // Local storage fallback for non-logged-in users
  const [localSettings, setLocalSettings] = useLocalStorage<A11ySettings>(
    "a11y-settings",
    {
      darkMode: false,
      highContrast: false,
      lowAnimation: false,
      dyslexiaFont: false,
      largeText: false,
      language: getBrowserPreferredLanguage(),
    }
  );

  // Initialize settings with better fallback handling
  const [settings, setSettings] = useState<A11ySettings>(() => {
    try {
      const storedSettings = localStorage.getItem("a11y-settings");

      if (storedSettings) {
        const parsed = JSON.parse(storedSettings);
        if (parsed.language) {
          return {
            darkMode: parsed.darkMode ?? false,
            highContrast: parsed.highContrast ?? false,
            lowAnimation: parsed.lowAnimation ?? false,
            dyslexiaFont: parsed.dyslexiaFont ?? false,
            largeText: parsed.largeText ?? false,
            language: parsed.language,
          };
        }
      }
    } catch (e) {
      console.warn("Failed to parse stored a11y settings:", e);
    }

    // Fallback to browser language or default
    try {
      const browserLang = getBrowserPreferredLanguage();
      return {
        darkMode: false,
        highContrast: false,
        lowAnimation: false,
        dyslexiaFont: false,
        largeText: false,
        language: browserLang,
      };
    } catch (e) {
      console.warn("Failed to get browser language, using default:", e);
      return defaultA11yContext.settings;
    }
  });

  // Update settings when user data changes or when switching between logged in/out
  useEffect(() => {
    if (session && userSettings?.preferences) {
      const translatedLanguage = translateLanguageNameToShortCode(
        userSettings.displayLanguage ?? "english"
      );
      const newSettings = {
        darkMode: userSettings.preferences.darkMode ?? false,
        highContrast: userSettings.preferences.highContrast ?? false,
        lowAnimation: userSettings.preferences.lowAnimation ?? false,
        dyslexiaFont: userSettings.preferences.dyslexiaFont ?? false,
        largeText: userSettings.preferences.largeText ?? false,
        language: translatedLanguage || "en",
      };
      setSettings(newSettings);
    } else if (!session) {
      const shouldUpdate =
        settings.darkMode !== localSettings.darkMode ||
        settings.highContrast !== localSettings.highContrast ||
        settings.lowAnimation !== localSettings.lowAnimation ||
        settings.dyslexiaFont !== localSettings.dyslexiaFont ||
        settings.largeText !== localSettings.largeText ||
        settings.language !== localSettings.language;

      if (shouldUpdate) {
        setSettings(localSettings);
      }
    }
  }, [session, userSettings, localSettings]); // Removed 'settings' from dependencies to prevent infinite loop

  useEffect(() => {
    const i18nextLanguage = translateA11yLanguageToI18next(settings.language);

    if (i18n.language !== i18nextLanguage) {
      i18n.changeLanguage(i18nextLanguage).catch((error) => {
        console.error(
          `A11yProvider: useEffect - Failed to change language to ${i18nextLanguage}:`,
          error
        );
      });
    }
  }, [settings.language, i18n]);

  // Apply settings to root element
  useEffect(() => {
    const root = document.documentElement;

    // Remove all accessibility classes first
    root.classList.remove(
      "dark",
      "high-contrast",
      "low-animation",
      "dyslexia-font",
      "large-text",
      "lang"
    );

    // Add active accessibility classes
    if (settings.darkMode) root.classList.add("dark");
    if (settings.highContrast) root.classList.add("high-contrast");
    if (settings.lowAnimation) root.classList.add("low-animation");
    if (settings.dyslexiaFont) root.classList.add("dyslexia-font");
    if (settings.largeText) root.classList.add("large-text");
    if (settings.language) {
      root.setAttribute("lang", settings.language);
    } else {
      root.removeAttribute("lang");
    }

    // Clean up on unmount
    return () => {
      root.classList.remove(
        "dark",
        "high-contrast",
        "low-animation",
        "dyslexia-font",
        "large-text",
        "lang"
      );
    };
  }, [settings]);

  // Update settings function with useCallback for stability
  const updateSettings = useCallback(
    (newSettings: Partial<A11ySettings>) => {
      const updatedSettings = { ...settings, ...newSettings };

      // Update settings state first
      setSettings(updatedSettings);

      // If not logged in, save to local storage
      if (!session) {
        setLocalSettings(updatedSettings);
      }

      // If language is being changed, update i18n immediately and explicitly
      if (newSettings.language && newSettings.language !== settings.language) {
        const i18nextLanguage = translateA11yLanguageToI18next(
          newSettings.language
        );

        // Use Promise to ensure language change happens
        i18n.changeLanguage(i18nextLanguage).catch((error) => {
          console.error(
            `A11yProvider: Failed to change language to ${i18nextLanguage} via updateSettings:`,
            error
          );
        });
      }

      // Note: If logged in, settings should be updated through the profile settings form
      // which will trigger a refetch of user settings
    },
    [settings, session, setLocalSettings, i18n]
  );

  const contextValue = useMemo<A11yContextType>(
    () => ({
      settings,
      updateSettings,
    }),
    [settings, updateSettings]
  );

  return (
    <A11yContext.Provider value={contextValue}>{children}</A11yContext.Provider>
  );
}
